import React, { useRef, useEffect, useState } from 'react';

import useBreakpoint from 'hooks/use-breakpoint';

import {
  generateLayoutMap,
  mapLayout,
} from 'containers/user/home/components/masonry-grid/masonry-grid.helper';
import type {
  MappedLayoutItem,
  MasonryWidget,
} from 'containers/user/home/components/masonry-grid/masonry-grid.helper';

import { useResponsiveColumns } from 'containers/user/home/components/masonry-grid/hooks/use-responsove-columns';

interface MasonryGridProps {
  widgets: MasonryWidget[];
}

const COLUMN_WIDTH_PX = 288;
const ROW_HEIGHT_PX = 38;
const MAX_COLUMNS = 4;

const MasonryGrid = ({ widgets }: MasonryGridProps): React.JSX.Element => {
  const gridRef = useRef<HTMLDivElement>(null);

  const currentBreakpoint = useBreakpoint();
  const columnCount = useResponsiveColumns(
    gridRef,
    COLUMN_WIDTH_PX,
    MAX_COLUMNS
  );
  const [layout, setLayout] = useState<MappedLayoutItem[]>([]);

  useEffect(() => {
    const { layoutMap, rowCount } = generateLayoutMap(widgets, columnCount);
    const finalLayout = mapLayout(
      widgets,
      layoutMap,
      columnCount,
      rowCount,
      currentBreakpoint
    );

    setLayout(finalLayout);
  }, [widgets, columnCount, currentBreakpoint]);

  return (
    <div
      ref={gridRef}
      className="grid py-4 transition-all ease-in-out"
      data-testid="masonry-grid"
      style={{
        gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
        gridAutoRows: `${ROW_HEIGHT_PX}px`,
      }}
    >
      {layout.map(({ key, widgetComponent, colSpan, rowSpan }) => (
        <div
          key={key}
          data-testid="masonry-item"
          className="m-1.5 rounded-lg shadow transition-all ease-in-out"
          style={{
            gridColumn: `span ${colSpan}`,
            gridRow: `span ${rowSpan}`,
          }}
        >
          {widgetComponent}
        </div>
      ))}
    </div>
  );
};

export default React.memo(MasonryGrid);
